import { ROLE, Space } from "@highnote/server/src/core/entities";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * `space.publicRoles` for every space doc this client ever saw.
 *
 * This cache exists because the permission resolver (`Auth/index.tsx`)
 * receives a `spaceId`, not an entity. `hasRole` adds the space's `public`
 * roles to every identity's role set, so the resolver needs those roles by
 * space id. `Auth/index.tsx` mounts this provider above
 * `AuthContextProvider`, because `GlobalSpacesContextProvider` sits below
 * that provider and so cannot be the home for this cache.
 *
 * A ref plus a version counter backs the cache, rather than state, because
 * `record` runs inside `onSnapshot` callbacks on hot paths and must not
 * re-render the whole app on every space snapshot. The counter only bumps
 * when a value actually changes.
 *
 * Known weakness: a permission question about a space whose doc this client
 * never saw resolves to `[]`. That is a strict under-grant (the user gets
 * fewer roles than they hold, never more), the same shape as today's failure
 * when `getGlobalSpace` misses. The server contains it: `canRead` already
 * includes the public union on the server side. So the access answer is
 * still right, and only fine-grained controls (UPLOAD on a public space, and
 * so on) under-grant.
 */
type PublicRolesContextValue = {
  getPublicRoles: (spaceId: Id) => ROLE[];
  record: (space?: Pick<Space, "id" | "publicRoles">) => void;
};

type RecordPublicRoles = PublicRolesContextValue["record"];

const EMPTY_ROLES: ROLE[] = [];

const noop: RecordPublicRoles = () => undefined;

const PublicRolesContext = createContext<PublicRolesContextValue>({
  getPublicRoles: () => EMPTY_ROLES,
  record: noop,
});

/**
 * The write half, deliberately a second context.
 *
 * The read value's identity must change when a `publicRoles` value changes,
 * because that change is what re-runs the permission resolver's memo. A
 * component that only writes must not join those re-renders, and React
 * context subscribes per provider value, not per field. The two writers
 * today (`useSpace`, `useWatcherV2`) sit on the hottest paths in the app, so
 * they take this context instead. The provider creates its value once, and
 * the value never changes.
 */
const RecordPublicRolesContext = createContext<RecordPublicRoles>(noop);

const sameRoles = (a: ROLE[], b: ROLE[]) =>
  a.length === b.length && a.every((role, index) => role === b[index]);

export const PublicRolesContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const rolesRef = useRef<Map<Id, ROLE[]>>(new Map());
  const [version, setVersion] = useState<number>(0);

  const record = useCallback((space?: Pick<Space, "id" | "publicRoles">) => {
    if (!space?.id) return;
    const next = space.publicRoles || EMPTY_ROLES;
    const prev = rolesRef.current.get(space.id);
    if (prev && sameRoles(prev, next)) return;
    rolesRef.current.set(space.id, next);
    setVersion((v) => v + 1);
  }, []);

  const getPublicRoles = useCallback(
    (spaceId: Id) => rolesRef.current.get(spaceId) || EMPTY_ROLES,
    /* `version` is in the deps on purpose: it is what changes the identity
     * of `getPublicRoles`, and that new identity is what re-runs the
     * resolver memo in `_AuthContextProvider` when a public-roles value
     * actually changes. */
    [version],
  );

  const value = useMemo(
    () => ({ getPublicRoles, record }),
    [getPublicRoles, record],
  );

  return (
    <RecordPublicRolesContext.Provider value={record}>
      <PublicRolesContext.Provider value={value}>
        {children}
      </PublicRolesContext.Provider>
    </RecordPublicRolesContext.Provider>
  );
};

/** The read side. It subscribes to public-roles changes; use it only if you
 *  read. */
export const usePublicRoles = () => useContext(PublicRolesContext);

/** The write side. It never re-renders the caller. */
export const useRecordPublicRoles = () => useContext(RecordPublicRolesContext);
